package com.macrofocus.eflux.ui.layer

import com.macrofocus.eflux.DataLoader
import com.macrofocus.eflux.deckgl.toJSonArray
import com.macrofocus.eflux.network.line.Line
import com.macrofocus.eflux.network.logical.LogicalBranchId
import com.macrofocus.eflux.ui.morphing.combinedMorphing
import com.macrofocus.eflux.ui.morphing.morphLinesParametric
import com.macrofocus.eflux.ui.morphing.swapOrientation
import deckgl.DashedPathLayer
import deckgl.PathLayer
import deckgl.PathLayerProps
import deckgl.PathStyleExtension
import js.objects.unsafeJso
import mui.material.styles.Theme
import org.locationtech.jts.geom.LineString
import org.mkui.color.crossplatform.brighter
import org.mkui.color.toDeckGL
import react.RefObject
import kotlin.js.Date
import kotlin.math.absoluteValue
import kotlin.math.max

fun createInteractiveLineLayer(
    dataLoader: DataLoader,
    lines: Array<Line>?,
    cartographic: Double,
    width: Double,
    theme: Theme,
    probed: LogicalBranchId?,
    selected: Array<LogicalBranchId>,
    lastClickTime: RefObject<Double>
): PathLayer<Line> {
    val controller = dataLoader.controller
    val doubleClickDelay = 300 // ms

    val lineLayer = PathLayer<Line>(unsafeJso {
        id = "interactive-line-layer"
        setPathLayerProperties(dataLoader, lines, cartographic)
//        opacity = 0.1 - (0.05 * (cartographic / 10.0))
        getWidth = { d: Line, info: dynamic ->
            val ratea = d.ratea
            val w = if (ratea != null && ratea.amount.absoluteValue > 0.0)
                1.5 + (width * max(0.0, (ratea.amount.absoluteValue / 1000.0)))
            else width * 0.1
            w * 5
        }
        widthMinPixels = 10 // Ensure minimum width
        widthMaxPixels = 10 // Ensure maximum width
        getColor = { d: Line, info: dynamic ->
            if (d.logicalBranchId == probed)
                arrayOf(253, 255, 180, 153)
            else if (selected.contains(d.logicalBranchId))
                arrayOf(253, 255, 180, 127)
            else
                arrayOf(255, 255, 255, 0)
        }
        autoHighlight = true
        highlightColor = arrayOf(253, 255, 180, 153)
        pickable = true
        onHover = { info ->
            val o = info.`object`
            if (o != null) {
                if (o is Line) {
                    val logicalBranchId = o.logicalBranchId
                    controller.probeLine(logicalBranchId, true)
                } else {
                    controller.probeLine(null, true)
                }
            } else {
                controller.probeLine(null, true)
            }
        }
        onClick = { info: dynamic ->
            val now = Date.now()
            val dbl = now - lastClickTime.current!! < doubleClickDelay
            lastClickTime.current = now

            val o = info.`object`
            if (o != null) {
                if (o is Line) {
                    if (dbl) {
                        dataLoader.physicalZoom(o.physicalGeometry)
                    }

                    val logicalBranchId = o.logicalBranchId
                    controller.selectLine(logicalBranchId, true)
                } else {
                    controller.selectLine(null, true)
                }
            } else {
                controller.selectLine(null, true)
            }
        }
        updateTriggers = unsafeJso {
            this.asDynamic().getPath = arrayOf(cartographic)
            this.asDynamic().getWidth = arrayOf(width, probed, selected)
            this.asDynamic().getColor = arrayOf(theme, probed, selected)
        }
        transitions = unsafeJso {
            this.asDynamic().getWidth = 50
            this.asDynamic().getColor = 50
        }
    })
    return lineLayer
}


fun createLineLayer(
    dataLoader: DataLoader,
    lines: Array<Line>?, cartographic: Double, width: Double, theme: Theme
): PathLayer<Line> {
    val lineLayer = PathLayer<Line>(unsafeJso {
        id = "line-layer"
        setPathLayerProperties(dataLoader, lines, cartographic)
        opacity = 0.1 - (0.05 * (cartographic / 10.0))
        getWidth = { d: Line, info: dynamic ->
            val ratea = d.ratea
            val w = if (ratea != null && ratea.amount.absoluteValue > 0.0)
                1.5 + (width * max(0.0, (ratea.amount.absoluteValue / 1000.0)))
            else width * 0.1
            w
        }
        widthMinPixels = 0.25 // Ensure minimum width
        widthMaxPixels = 20 // Ensure maximum width
        getColor = arrayOf(127, 127, 127, 127)
        updateTriggers = unsafeJso {
            this.asDynamic().getPath = arrayOf(cartographic)
            this.asDynamic().getWidth = arrayOf(width)
            this.asDynamic().getColor = arrayOf(theme)
        }
    })
    return lineLayer
}

fun createDashedLineLayer(
    dataLoader: DataLoader,
    lines: Array<Line>?,
    cartographic: Double,
    width: Double,
    theme: Theme,
    currentTime: Double,
    probed: LogicalBranchId?
): DashedPathLayer<Line> {
    val kv380 = theme.asDynamic().kv380
    val kv220 = theme.asDynamic().kv220
    val kv150 = theme.asDynamic().kv150
    val outOfService = theme.asDynamic().outOfService
    val invalid = theme.asDynamic().invalid

//    val gl = js("WebGLRenderingContext") // Access WebGL constants

    val lineLayer = DashedPathLayer<Line>(unsafeJso {
        id = "dashed-line-layer"
        setPathLayerProperties(dataLoader, lines, cartographic)

        opacity = 1.0 - (0.5 * (cartographic / 10.0))
        getWidth = { d: Line, info: dynamic ->
//            console.log(info)
//            println("Info: $info, ${info::class}")
            // Calculate line width based on zoom level
            //           val zoom = info.viewport.zoom as Double
//            pow(2.0, zoom - 10.0) // Adjust scaling factor as needed
//            if(d.pratea != null)
//                props.width * d.pratea
//            else props.width * 0.1
            val dp = d.dp
            val w = if (dp != null && dp.amount.absoluteValue > 0.0)
                1.5 + (width * max(0.0, (dp.amount.absoluteValue / 1000.0)))
            else width * 0.1
            w
        }
        widthMinPixels = 0.25 // Ensure minimum width
        widthMaxPixels = 20 // Ensure maximum width
        getColor = { line: Line ->
            val c = dataLoader.getColor(line, theme)
            if (line.logicalBranchId == probed) {
                c.brighter().toDeckGL()
            } else {
                c.toDeckGL()
            }
//            val name = line.name
//            val c = if (name == null) invalid
//            else if(line.dp != null && line.dp.amount.absoluteValue == 0.0) outOfService
//            else if (name.contains("380 kV")) kv380
//            else if (name.contains("220 kV")) kv220
//            else if (name.contains("150 kV")) kv150
//            else invalid
//            c
        }
//        val blendFunc = arrayOf(GL.SRC_ALPHA, GL.ONE_MINUS_SRC_ALPHA) // Normal alpha blending
//        val blendFunc = arrayOf(GL.SRC_ALPHA, GL.ONE, GL.ONE_MINUS_DST_ALPHA, GL.ONE)
//        val blendFunc = arrayOf(gl.ONE, gl.ONE) // Additive blending
//        val blendEquation = GL.FUNC_ADD
//        val blendEquation = GL.MAX
//        asDynamic().parameters = unsafeJso {
//            this.blendFunc = blendFunc
//            this.blendEquation = blendEquation
//        }
        asDynamic().getDashArray = { l: Line ->
            val pratea = l.pratea
            if (pratea != null)
                if (pratea > 0.0)
                    arrayOf(3.0 / pratea, 1)
                else {
                    emptyArray()
                }
            else
                arrayOf(5, 1)
        }
//        getWidth = { d: dynamic -> (Date.now() / 100) % 5 }
//        asDynamic().getDashArray = arrayOf(currentTime % 10, currentTime % 10)
//        asDynamic().getOffset = (Date.now() / 100) % 5;
        asDynamic().dashStart = currentTime;
        extensions = arrayOf(PathStyleExtension(unsafeJso {
            dashMode = "path"
            offset = true
        }))
        autoHighlight = false
        highlightColor = arrayOf(253, 255, 180, 153)
        updateTriggers = unsafeJso {
            this.asDynamic().getPath = arrayOf(cartographic)
            this.asDynamic().getWidth = arrayOf(width)
            this.asDynamic().getColor = arrayOf(theme, probed)
        }
    })
    return lineLayer
}

private fun PathLayerProps<Line>.setPathLayerProperties(
    dataLoader: DataLoader,
    lines: Array<Line>?,
    cartographic: Double
) {
    this.data = if (lines != null) lines else emptyArray<Array<Line>>()
    getPath = { d: Line ->
        val result: Array<Array<Double>>?
//        with(measureTime {
            val morphed: Array<Array<Double>>?

            val physical = d.physicalGeometry as? LineString
            val logical = d.logicalGeometry as? LineString

            if (physical != null) {
                if (cartographic <= 0) {
                    morphed = d.logicalGeometryArray
                } else if (cartographic >= 10) {
                    morphed = d.physicalGeometryArray
                } else {
                    if (logical != null) {
                        val combinedLogical = d.combinedLogical
                        val combinedPhysical = d.combinedPhysical
                        if (combinedLogical.numPoints != combinedPhysical?.numPoints) {
//                            println("morphLinesParametric ${d.logicalBranchId}")
                            morphed = morphLinesParametric(logical, physical, cartographic, 10).toJSonArray()
                        } else {
                            morphed = combinedMorphing(combinedLogical, combinedPhysical, cartographic, 10).toJSonArray()
                        }
                    } else {
                        morphed = d.physicalGeometryArray
                    }
                }
            } else {
                if (cartographic <= 10 / 2) {
                    morphed = d.logicalGeometryArray
                } else {
                    morphed = null
                }
            }

        val dp = d.dp
        if (morphed == null || (dp != null && dp.amount <= 0.0)) {
                result = morphed
            } else {
                result = swapOrientation(morphed)
            }
//        }) {
//            println("getPath for ${d.logicalBranchId} in $this")
//        }
        if (result != null) {
            result
        } else {
            emptyArray()
        }
    }
    widthUnits = "pixels"
}